import React, { useState, useEffect } from 'react';
import { Smartphone, Laptop, Monitor, CheckCircle, XCircle, Wrench, Users, Clock, Zap, Sparkles } from 'lucide-react';

type RoomState = 'available' | 'occupied' | 'maintenance';

const AppSyncAnimation: React.FC = () => {
  const [currentState, setCurrentState] = useState<RoomState>('available');
  const [isChanging, setIsChanging] = useState(false);
  const [showParticles, setShowParticles] = useState(false);
  const [pulseEffect, setPulseEffect] = useState(false);

  const states: RoomState[] = ['available', 'occupied', 'maintenance'];
  
  const stateConfig = {
    available: {
      color: '#22c55e',
      bgColor: '#dcfce7',
      emoji: '✅',
      text: 'Available',
      icon: <CheckCircle className="w-4 h-4" />,
      gradient: 'from-green-400 to-emerald-500'
    },
    occupied: {
      color: '#ef4444',
      bgColor: '#fee2e2',
      emoji: '🔴',
      text: 'Occupied',
      icon: <XCircle className="w-4 h-4" />,
      gradient: 'from-red-400 to-pink-500'
    },
    maintenance: {
      color: '#f59e0b',
      bgColor: '#fef3c7',
      emoji: '🔧',
      text: 'Maintenance',
      icon: <Wrench className="w-4 h-4" />,
      gradient: 'from-yellow-400 to-orange-500'
    }
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setIsChanging(true);
      setShowParticles(true);
      setPulseEffect(true);
      
      setTimeout(() => {
        setCurrentState((prev) => {
          const currentIndex = states.indexOf(prev);
          return states[(currentIndex + 1) % states.length];
        });
        setIsChanging(false);
        setPulseEffect(false);
        setTimeout(() => setShowParticles(false), 1000);
      }, 300);
    }, 4000);

    return () => clearInterval(interval);
  }, []);

  const currentConfig = stateConfig[currentState];

  const RoomCard = ({ isCompact = false }: { isCompact?: boolean }) => (
    <div 
      className={`rounded-xl border-2 transition-all duration-500 overflow-hidden relative ${
        isChanging ? 'scale-105 shadow-2xl' : 'scale-100'
      } ${pulseEffect ? 'animate-pulse' : ''}`}
      style={{ 
        borderColor: currentConfig.color,
        backgroundColor: currentConfig.bgColor,
        boxShadow: isChanging ? `0 0 30px ${currentConfig.color}40` : 'none'
      }}
    >
      {/* Animated background gradient */}
      <div className={`absolute inset-0 bg-gradient-to-br ${currentConfig.gradient} opacity-10 transition-opacity duration-500 ${isChanging ? 'opacity-20' : 'opacity-10'}`} />
      
      {/* Sparkle effect */}
      {isChanging && (
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-2 right-2 animate-spin">
            <Sparkles className="w-4 h-4 text-yellow-400" />
          </div>
          <div className="absolute bottom-2 left-2 animate-spin" style={{ animationDelay: '0.5s' }}>
            <Sparkles className="w-3 h-3 text-blue-400" />
          </div>
        </div>
      )}

      <div className="p-3 relative z-10">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-semibold text-slate-800 text-sm">House</h3>
          <div className="flex items-center gap-1 text-xs text-slate-600">
            <Users className="w-3 h-3" />
            <span>3 roommates</span>
          </div>
        </div>
        
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div 
              className={`w-8 h-8 rounded-lg flex items-center justify-center text-white transition-all duration-500 ${
                isChanging ? 'animate-bounce' : ''
              }`}
              style={{ backgroundColor: currentConfig.color }}
            >
              <span className="text-sm">{currentConfig.emoji}</span>
            </div>
            <div>
              <div className="font-medium text-slate-800 text-sm">{currentConfig.text}</div>
              <div className="flex items-center gap-1 text-xs text-slate-600">
                <Clock className="w-3 h-3" />
                <span>Updated now</span>
              </div>
            </div>
          </div>
          
          {!isCompact && (
            <div className="flex items-center gap-1">
              {currentConfig.icon}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const DesktopApp = () => (
    <div className="w-80 h-64 bg-white dark:bg-slate-800 rounded-xl shadow-xl border border-slate-200 dark:border-slate-700 overflow-hidden relative">
      {/* Floating particles */}
      {showParticles && (
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-4 left-4 w-2 h-2 bg-blue-400 rounded-full animate-ping" />
          <div className="absolute top-8 right-8 w-1.5 h-1.5 bg-purple-400 rounded-full animate-ping" style={{ animationDelay: '0.2s' }} />
          <div className="absolute bottom-8 left-8 w-1 h-1 bg-green-400 rounded-full animate-ping" style={{ animationDelay: '0.4s' }} />
        </div>
      )}

      {/* App Header */}
      <div className="bg-gradient-to-r from-blue-500 to-purple-600 text-white p-4 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent animate-pulse" />
        <div className="relative z-10 flex items-center justify-between">
          <h2 className="font-bold text-lg">RoomState</h2>
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
            <span className="text-sm">Online</span>
          </div>
        </div>
      </div>
      
      {/* App Content */}
      <div className="p-4 space-y-3">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-slate-800 dark:text-slate-200">Rooms</h3>
          <div className="text-xs text-slate-500">3 rooms</div>
        </div>
        
        <RoomCard />
        
        <div className="grid grid-cols-2 gap-2">
          <div className="p-2 bg-slate-50 dark:bg-slate-700 rounded-lg hover:scale-105 transition-transform duration-200">
            <div className="text-xs text-slate-600 dark:text-slate-400">Call Availability</div>
            <div className="text-sm font-medium text-green-600">Available</div>
          </div>
          <div className="p-2 bg-slate-50 dark:bg-slate-700 rounded-lg hover:scale-105 transition-transform duration-200">
            <div className="text-xs text-slate-600 dark:text-slate-400">Birthday Mode</div>
            <div className="text-sm font-medium text-red-600">Occupied</div>
          </div>
        </div>
      </div>
    </div>
  );

  const LaptopApp = () => (
    <div className="w-64 h-48 bg-white dark:bg-slate-800 rounded-lg shadow-lg border border-slate-200 dark:border-slate-700 overflow-hidden relative">
      {/* Floating particles */}
      {showParticles && (
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-2 left-2 w-1.5 h-1.5 bg-blue-400 rounded-full animate-ping" />
          <div className="absolute bottom-2 right-2 w-1 h-1 bg-purple-400 rounded-full animate-ping" style={{ animationDelay: '0.3s' }} />
        </div>
      )}

      {/* App Header */}
      <div className="bg-gradient-to-r from-blue-500 to-purple-600 text-white p-3 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent animate-pulse" />
        <div className="relative z-10 flex items-center justify-between">
          <h2 className="font-bold text-sm">RoomState</h2>
          <div className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse"></div>
        </div>
      </div>
      
      {/* App Content */}
      <div className="p-3 space-y-2">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-semibold text-slate-800 dark:text-slate-200 text-sm">Rooms</h3>
          <div className="text-xs text-slate-500">3</div>
        </div>
        
        <RoomCard />
        
        <div className="flex gap-1">
          <div className="flex-1 p-2 bg-slate-50 dark:bg-slate-700 rounded text-center hover:scale-105 transition-transform duration-200">
            <div className="text-xs text-slate-600 dark:text-slate-400">Call Availability</div>
            <div className="text-xs font-medium text-green-600">Free</div>
          </div>
          <div className="flex-1 p-2 bg-slate-50 dark:bg-slate-700 rounded text-center hover:scale-105 transition-transform duration-200">
            <div className="text-xs text-slate-600 dark:text-slate-400">Birthday Mode</div>
            <div className="text-xs font-medium text-red-600">Busy</div>
          </div>
        </div>
      </div>
    </div>
  );

  const MobileApp = () => (
    <div className="w-32 h-56 bg-white dark:bg-slate-800 rounded-2xl shadow-lg border border-slate-200 dark:border-slate-700 overflow-hidden relative">
      {/* Floating particles */}
      {showParticles && (
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1 left-1 w-1 h-1 bg-blue-400 rounded-full animate-ping" />
          <div className="absolute bottom-1 right-1 w-0.5 h-0.5 bg-purple-400 rounded-full animate-ping" style={{ animationDelay: '0.2s' }} />
        </div>
      )}

      {/* App Header */}
      <div className="bg-gradient-to-r from-blue-500 to-purple-600 text-white p-2 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent animate-pulse" />
        <div className="relative z-10 flex items-center justify-between">
          <h2 className="font-bold text-xs">RoomState</h2>
          <div className="w-1 h-1 bg-green-400 rounded-full animate-pulse"></div>
        </div>
      </div>
      
      {/* App Content */}
      <div className="p-2 space-y-2">
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-semibold text-slate-800 dark:text-slate-200 text-xs">Rooms</h3>
          <div className="text-xs text-slate-500">3</div>
        </div>
        
        <div 
          className={`rounded-lg border-2 p-2 transition-all duration-500 ${
            isChanging ? 'animate-bounce' : ''
          }`}
          style={{ 
            borderColor: currentConfig.color,
            backgroundColor: currentConfig.bgColor,
            boxShadow: isChanging ? `0 0 15px ${currentConfig.color}40` : 'none'
          }}
        >
          <div className="text-xs font-medium text-slate-800 mb-1">House</div>
          <div className="flex items-center gap-1">
            <div 
              className="w-4 h-4 rounded flex items-center justify-center text-white text-xs"
              style={{ backgroundColor: currentConfig.color }}
            >
              {currentConfig.emoji}
            </div>
            <span className="text-xs font-medium text-slate-800">{currentConfig.text}</span>
          </div>
        </div>
        
        <div className="space-y-1">
          <div className="p-1 bg-slate-50 dark:bg-slate-700 rounded text-center hover:scale-105 transition-transform duration-200">
            <div className="text-xs text-slate-600 dark:text-slate-400">Call Availability</div>
            <div className="text-xs font-medium text-green-600">Free</div>
          </div>
          <div className="p-1 bg-slate-50 dark:bg-slate-700 rounded text-center hover:scale-105 transition-transform duration-200">
            <div className="text-xs text-slate-600 dark:text-slate-400">Birthday Mode</div>
            <div className="text-xs font-medium text-red-600">Busy</div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="relative p-8 bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm rounded-2xl shadow-xl border border-slate-200/50 dark:border-slate-700/50 overflow-hidden">
      {/* Background animated elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-10 -right-10 w-20 h-20 bg-gradient-to-br from-blue-400/20 to-purple-400/20 rounded-full blur-xl animate-float" />
        <div className="absolute -bottom-10 -left-10 w-16 h-16 bg-gradient-to-br from-purple-400/20 to-pink-400/20 rounded-full blur-xl animate-float" style={{ animationDelay: '-2s' }} />
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-32 h-32 bg-gradient-to-br from-green-400/10 to-blue-400/10 rounded-full blur-2xl animate-pulse" />
      </div>

      {/* Header */}
      <div className="text-center mb-8 relative z-10">
        <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-200 mb-2">
          Instant App Synchronization
        </h3>
        <p className="text-sm text-slate-600 dark:text-slate-400">
          Change the room status on any device and watch it update instantly across all apps
        </p>
      </div>

      {/* Devices with Apps */}
      <div className="flex items-center justify-center gap-8 mb-6 relative z-10">
        {/* Desktop */}
        <div className="flex flex-col items-center group">
          <div className="mb-3 group-hover:scale-110 transition-transform duration-300">
            <Monitor className="w-8 h-8 text-slate-600 dark:text-slate-400" />
          </div>
          <DesktopApp />
          <span className="mt-3 text-sm font-medium text-slate-700 dark:text-slate-300">Desktop</span>
        </div>

        {/* Laptop */}
        <div className="flex flex-col items-center group">
          <div className="mb-3 group-hover:scale-110 transition-transform duration-300">
            <Laptop className="w-8 h-8 text-slate-600 dark:text-slate-400" />
          </div>
          <LaptopApp />
          <span className="mt-3 text-sm font-medium text-slate-700 dark:text-slate-300">Laptop</span>
        </div>

        {/* Mobile */}
        <div className="flex flex-col items-center group">
          <div className="mb-3 group-hover:scale-110 transition-transform duration-300">
            <Smartphone className="w-8 h-8 text-slate-600 dark:text-slate-400" />
          </div>
          <MobileApp />
          <span className="mt-3 text-sm font-medium text-slate-700 dark:text-slate-300">Mobile</span>
        </div>
      </div>

      {/* Status Indicator */}
      <div className="text-center relative z-10">
        <div 
          className={`inline-flex items-center gap-3 px-6 py-3 rounded-xl text-white font-medium transition-all duration-500 ${
            isChanging ? 'scale-110 shadow-2xl' : 'scale-100'
          }`}
          style={{ 
            backgroundColor: currentConfig.color,
            boxShadow: isChanging ? `0 0 30px ${currentConfig.color}60` : 'none'
          }}
        >
          {currentConfig.icon}
          <span>{currentConfig.text}</span>
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
          </div>
          {isChanging && (
            <div className="absolute -top-1 -right-1">
              <Zap className="w-4 h-4 text-yellow-300 animate-bounce" />
            </div>
          )}
        </div>
        
        <div className="mt-4 text-sm text-slate-600 dark:text-slate-400">
          <span>All devices show the same real-time information</span>
        </div>
      </div>

      {/* Feature Highlight */}
      <div className="mt-6 p-4 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-lg relative z-10 hover:scale-105 transition-transform duration-300">
        <div className="flex items-start gap-3">
          <div className="w-6 h-6 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 animate-pulse">
            <span className="text-blue-600 dark:text-blue-400 text-xs font-bold">⚡</span>
          </div>
          <div className="text-sm text-slate-600 dark:text-slate-400">
            <p className="font-medium mb-1">Real-time Updates</p>
            <p className="text-xs">No refresh needed. Changes appear instantly across all your devices and team members.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AppSyncAnimation; 